/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { ChatItem } from '@src/app/model/chat-item';
import { Field } from '@src/app/shared/field/Field';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { AppFormControl, ControlOn } from '@src/app/shared/form/app-form-control';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { ChatField } from '@src/app/shared/chat/chat-field';
import { ChatUpsertComponent } from '@src/app/shared/chat/chat-upsert/chat-upsert.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { Privilege } from '@src/app/model/role';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatEntryComponent } from '@src/app/shared/chat/chat-entry/chat-entry.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ChatFollowersComponent } from '@src/app/shared/chat/chat-followers/chat-followers.component';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { AddFollower } from '@src/app/shared/chat/add-follower';


@Component({
    selector: 'app-chat',
    standalone: true,
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    imports: [MatDialogContent, NgTemplateOutlet, ChatFollowersComponent, MatButtonModule, MatIconModule, ChatEntryComponent, MatTooltipModule, MatMenuModule, MatDividerModule, NgClass]
})
export class ChatComponent {
    private dialog = inject(MatDialog);
    private chatItemSvc = inject(UnitService);
    private currentUserSvc = inject(CurrentUserService);
    private cds = inject(ConfirmDialogService);
    dialogRef = inject<MatDialogRef<ChatComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<{
    chatField: ChatField;
    addNew: boolean;
}>(MAT_DIALOG_DATA, { optional: true });


    hierarchyItems: ChatItem[] = [];

    @Input() label: string;
    @Input() control: AppFormControl;
    @Input() on: ControlOn = 'form';

    chatField: ChatField;

    chatItems: ChatItem[] = [];

    isPhone = false;

    personPrivilege: Privilege;
    addOthers = false;

    constructor() {
        const isNarrowSvc = inject(IsNarrowService);
        const dialogOptions = this.dialogOptions;


        if (dialogOptions?.chatField) {
            this.chatField = dialogOptions.chatField;
            this.control = this.chatField.control as AppFormControl;

            if (dialogOptions.addNew) {
                /* No idea why this might ever have been required...
                const chat = this.chatField.chat;
                if (this.getChatItems().length === 0
                && chat.followers.length === 1
                && chat.followers[0].userId === currentUserSvc.currentUser.id) {
                    this.escalateToManagement();
                } else {
                    this.chatUpsert({chatItem: null});
                }
                */
                this.chatUpsert({ chatItem: null });
            }
        }

        isNarrowSvc.detectVeryNarrow().pipe(takeUntilDestroyed()).subscribe(result => { this.isPhone = result });

        /* for now disabling addOthers - it does not refresh the mail to action list..
        this.currentUserSvc.getCurrentUser().subscribe( () => {
            if (this.currentUserSvc.currentUser.inAgency) {
                this.addOthers = true; // May still get error, but server will prevent if not actually allowed
                // Should really set this when setting the value, so we can allow for current team...
            } else {
                const priv = User.getPathPrivilege(this.currentUserSvc.currentUser, PersonPageComponent.navRoute.url);
                if (priv.getIndex) {
                    this.addOthers = true;
                }
            }
        })
        */
    }

    public static make(service: AbstractHttpService, options: Partial<ChatField> = {}, allowReplies = true) {
        const field: ChatField = new ChatField(service,
            { label: 'Conversation', value: 'comments', visible: Field.noShow, sendServer: false },
            options
        );
        field.chat.allowReplies = allowReplies;
        field.formControlFactory = ChatComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: AppFormControl, on: ControlOn) {
        const componentRef = vcr.createComponent(ChatComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        componentRef.instance.chatField = ctl.field as ChatField;
        return componentRef;
    }

    follow() {
        this.cds.open('Follow this item', 'Do you want to receive mail notifications for updates and comments on this item', () => {
            const chat = this.chatField.chat;
            this.chatField.chat.service.follow(chat.parentId, this.currentUserSvc.currentUser.id, null, chat.teamId).subscribe(
                (f) => {
                    if (f) {
                        this.chatField.chat.followers.push(f);
                    }
            })
        });
    }

    isFollowing(): boolean {
        if (this.chatField.chat.followers.find((f) => f.personUserId === this.currentUserSvc.currentUser.id)) {
            return true;
        } else {
            return false;
        }
    }

    stopFollowing() {
        this.cds.open('Stop Following', 'Do you want to stop receiving mail notifications for updates and comments on this item',
            () => {
                const idx = this.chatField.chat.followers.findIndex(
                    (f) => f.personUserId === this.currentUserSvc.currentUser.id
                );
                if (idx < 0) {
                    this.cds.alert('Cannot stip following', 'you are not following in the first place');
                } else {
                    this.chatField.chat.service.stopFollowing(this.chatField.chat.followers[idx]).subscribe((success) => {
                        if (success) {
                            this.chatField.chat.followers.splice(idx, 1);
                        }
                    });
                }
            });
    }

    /*
    addChatItem() {
        this.upsertChatItem();

        //this.upsertAction().subscribe(o => {
        //  this.actionsField.control.addRow(o, true, false);
        //});

    }
    */
    getChatItems() {
        return (this.control.field as ChatField).chat.hierarchyItems;
    }

    private getChatItemFields(): Field[] {
        console.log('Creating field', { comp: this, fld: this.chatField });
        const flds = [];

        const parentName = this.chatField.chat.parentTitle;

        flds.push(FieldMaker.id());
        flds.push(FieldMaker.rev());
        flds.push(FieldMaker.idHolder('requestId'));
        flds.push(FormTextComponent.make('Parent', 'parent',
            { readonly: true, calculateValue: () => parentName })
        );
        flds.push(FormRichTextComponent.make('Comment', 'content', { cellOpts: { width: '50%' } }));

        return flds;
    }

    private createNewCI(): Observable<ChatItem> {
        return of(new ChatItem());
    }

    escalateToManagement() {
        this.cds.open('Escalate to management', 'Add all members of the management team', () => {
            const chat = this.chatField.chat;
            this.chatField.chat.service.escalate(chat.parentId, chat.followers, chat.teamId).subscribe(() => {
                console.log('successful');
            });
        });
    }

    addPersonAsFollower(addFromAgentTeam = false) {
        (new AddFollower(this.chatField)).addPersonAsFollower(addFromAgentTeam);
    }

    chatUpsert($event) {
        const dialogRef = this.dialog.open(ChatUpsertComponent,
            {
                data:
                {
                    chatField: this.control.field,
                    replyTo: $event.chatItem,
                }
            }
        );

        dialogRef.afterClosed().subscribe(newComment => {
            /* If showing comments in a pop up modal, with zero comments
            and no comment was added, better close the comments modal...
            */
            if (this.dialogRef && !newComment && this.chatField.chat.comments.length === 0) {
                this.dialogRef.close();
            }
        });
    }

    /*
    upsertChatItem(ci: ChatItem = null) {

        const fields = this.getChatItemFields();

        const dialogFormConfig = new FormConfig(
            {
                title: $localize`Comment for [${this.chatField.chat.parentTitle}]`,
                fieldSet: new FieldSet(
                    {
                        fields: fields,
                        formLayout: [{ cells: [{}] }]
                    }
                ),
                mode: ci ? 'edit' : 'new',
                objectFactory: this.createNewCI.bind(this),
            }
        );

        const dialogRef = this.dialog.open(EditDialogComponent,
            {
                data:
                {
                    config: dialogFormConfig,
                    service: this.chatItemSvc,
                    id: ci ? ci.id : null,
                }
            }
        );

        return dialogRef.afterClosed();

        //dialogRef.afterClosed().pipe(first()).pipe<Action>(map(o => {
        //    return o;
        //}));

    }
*/
    setSortMode(ascending = true) {
        this.chatField.chat.mode.order = ascending ? 'ascending' : 'descending';
        this.chatField.sortChat();
    }

    setNested(nested) {
        this.chatField.chat.mode.nested = nested;
        this.chatField.sortChat();
        /* Expands all...
        for (const ce of this.chatField.chat.hierarchyItems) {
            ce.showReplies = nested;
        }
        */
    }
}
